import { useCallback, useEffect, useRef, useState } from 'react'
import {
  parseSharedRoomMatchMeta,
  type SharedRoomMatchMeta,
} from '@/pages/Meetings/utils/sharedRoomPresentation'

/**
 * Where an offer to merge into a shared room has got to.
 *
 * `closed` is the only state that outlives the offer, and only for the two
 * reasons worth telling the reader about — the server also closes an offer
 * when a machine leaves or when the group changed under it, and neither is
 * something anybody did.
 *
 * `offered` and `requested` are the same moment seen from the two sides of a
 * merge: the machine already in the room is asked, and the machine that has to
 * move into it is only told. Which one this is comes from the server.
 */
export type SharedRoomOfferState =
  | { kind: 'idle' }
  | { kind: 'offered'; matchId: string; peerNames: string[]; meta: SharedRoomMatchMeta }
  | { kind: 'requested'; matchId: string; peerNames: string[]; meta: SharedRoomMatchMeta }
  | {
      kind: 'waiting'
      matchId: string
      peerNames: string[]
      meta: SharedRoomMatchMeta
      /** Set when waiting on `sharedroom.rejoin` rather than a merge reply. */
      rejoinRoomId?: string
    }
  | { kind: 'joined'; roomId: string; peerNames: string[]; meta: SharedRoomMatchMeta }
  | { kind: 'closed'; reason: 'declined' | 'failed' | 'expired' }

/** Snapshot that keeps the Similar·Join chip after Không or Leave (handoff 84). */
export type SharedRoomDismissedMatch = {
  source: 'declined' | 'left'
  /** Required for `declined` — sent again via `match_reply`. */
  matchId?: string
  /** Required for `left` — sent via `sharedroom.rejoin`. */
  roomId?: string
  peerNames: string[]
  meta: SharedRoomMatchMeta
}

/** How long Yes waits for `auto_join` before treating the attempt as failed. */
export const SHARED_ROOM_JOIN_WAIT_MS = 15_000

/**
 * The states an offer is still live in, and so the only ones a close can end.
 *
 * A type guard rather than a boolean because the callers go on to read
 * `matchId`, which exists on exactly these three.
 */
function isOpen(
  state: SharedRoomOfferState
): state is Extract<SharedRoomOfferState, { kind: 'offered' | 'requested' | 'waiting' }> {
  return state.kind === 'offered' || state.kind === 'requested' || state.kind === 'waiting'
}

function metaFromState(
  state: Extract<SharedRoomOfferState, { kind: 'offered' | 'requested' | 'waiting' }>
): SharedRoomMatchMeta {
  return state.meta
}

interface UseSharedRoomOfferOptions {
  /** Answers on the open socket; false when there is none left to answer on. */
  sendReply: (matchId: string, accept: boolean) => boolean
  /** Asks the AI service to put this machine back into a room it left. */
  sendRejoin: (roomId: string) => boolean
  /** Moves this session onto the room, keeping the capture. */
  rejoin: (roomId: string) => boolean
  /**
   * The room the reader's meeting already names, if any.
   *
   * A merge is announced once, as a frame. Nothing replays it, so after a
   * reload the only thing that still knows about the room is the stored
   * `sharedRoomId` — and without reading it here the reader comes back merged
   * but with no banner and, worse, no way out of the room at all.
   */
  currentRoomId: string | null | undefined
  /**
   * Records the room on the reader's own meeting, so a reload stays in it.
   *
   * `onlyWhenUnset` is the whole difference between the two merge frames: the
   * machine that moves takes the new room, while the machine that stays keeps
   * whatever its meeting already names — its own id IS the room.
   */
  onJoined: (roomId: string, options: { onlyWhenUnset: boolean }) => void
  /** Fired when `currentRoomId` restores a persisted merge after reload (not a live join). */
  onPersistedRoomRestore?: () => void
  /**
   * Initial Join-chip snapshot (e.g. from sessionStorage after reload).
   * Ignored when `currentRoomId` already restores a joined room.
   */
  initialDismissedMatch?: SharedRoomDismissedMatch | null
  /** Notified when the Join-chip snapshot appears, updates, or clears. */
  onDismissedMatchChange?: (snapshot: SharedRoomDismissedMatch | null) => void
  /**
   * Rejoin after Leave was cancelled or timed out before `auto_join`.
   * Lets the screen restore guards that block late merge frames.
   */
  onRejoinAbandoned?: () => void
}

/** The shape of the frames this hook acts on, picked out of an unknown message. */
function readFrame(frame: unknown): {
  event: string
  matchId?: string
  roomId?: string
  reason?: string
  role?: string
  peerNames: string[]
  meta: SharedRoomMatchMeta
  membersFieldPresent: boolean
} | null {
  if (typeof frame !== 'object' || frame === null) return null
  const record = frame as Record<string, unknown>
  if (typeof record.event !== 'string') return null
  const names = Array.isArray(record.peerNames)
    ? record.peerNames.filter((name): name is string => typeof name === 'string')
    : []
  return {
    event: record.event,
    matchId: typeof record.matchId === 'string' ? record.matchId : undefined,
    roomId: typeof record.roomId === 'string' ? record.roomId : undefined,
    reason: typeof record.reason === 'string' ? record.reason : undefined,
    role: typeof record.role === 'string' ? record.role : undefined,
    peerNames: names,
    meta: parseSharedRoomMatchMeta(record),
    membersFieldPresent: Array.isArray(record.members),
  }
}

const EMPTY_META: SharedRoomMatchMeta = {}

type JoinedOfferState = Extract<SharedRoomOfferState, { kind: 'joined' }>

type RoomSnapshotPayload = { peerNames: string[]; meta: SharedRoomMatchMeta }

type RoomSnapshotMessage = RoomSnapshotPayload & { membersFieldPresent?: boolean }

type RoomSnapshotOptions = { membersFieldPresent?: boolean }

type RoomSnapshotState = Extract<SharedRoomOfferState, { peerNames: string[] }>

function roomSnapshotHasUpdates(
  message: RoomSnapshotPayload,
  options?: RoomSnapshotOptions
): boolean {
  return (
    message.peerNames.length > 0 ||
    Boolean(message.meta.members?.length) ||
    Boolean(message.meta.peerMeetingTitle?.trim()) ||
    Boolean(message.meta.roomProject?.key.trim()) ||
    options?.membersFieldPresent === true
  )
}

function mergeRoomSnapshot<T extends { peerNames: string[]; meta: SharedRoomMatchMeta }>(
  current: T,
  message: RoomSnapshotPayload,
  options?: RoomSnapshotOptions
): T | null {
  if (!roomSnapshotHasUpdates(message, options)) return null
  const peerNames =
    message.peerNames.length > 0
      ? message.peerNames
      : options?.membersFieldPresent
        ? message.peerNames
        : current.peerNames
  const meta = mergePeerMeetingMeta(current.meta, message.meta)
  return { ...current, peerNames, meta }
}

/** BrSE may repeat `match_found`; later frames often omit fields the first already sent. */
function applyPeerTitlePatch(
  current: SharedRoomOfferState,
  message: RoomSnapshotPayload
): RoomSnapshotState | null {
  if (!message.meta.peerMeetingTitle?.trim()) return null
  if (isOpen(current) || current.kind === 'joined') {
    return mergeRoomSnapshot(current, message)
  }
  return null
}

/** BrSE may repeat `match_found`; later frames often omit fields the first already sent. */
function mergePeerMeetingMeta(
  baseMeta: SharedRoomMatchMeta,
  incomingMeta: SharedRoomMatchMeta
): SharedRoomMatchMeta {
  const merged = { ...baseMeta, ...incomingMeta }
  if (!incomingMeta.peerMeetingTitle?.trim() && baseMeta.peerMeetingTitle?.trim()) {
    merged.peerMeetingTitle = baseMeta.peerMeetingTitle
  }
  if (!incomingMeta.roomProject?.key.trim() && baseMeta.roomProject?.key.trim()) {
    merged.roomProject = baseMeta.roomProject
  }
  return merged
}

/**
 * The previous room's name, dropped on the way into another one.
 *
 * `peerMeetingTitle` names the room it arrived with, not the machine that
 * joined it. A move into a room BrSE has not named yet would otherwise put the
 * name of the room just left over the new one — a title that is certain to be
 * wrong, on a meeting that is no longer the one it described.
 */
function carryMetaIntoRoom(
  meta: SharedRoomMatchMeta,
  previousRoomId: string | null | undefined,
  nextRoomId: string
): SharedRoomMatchMeta {
  if (!previousRoomId || previousRoomId === nextRoomId) return meta
  const carried = { ...meta }
  delete carried.peerMeetingTitle
  delete carried.roomProject
  return carried
}

function mergeMatchFoundPayload(
  previousMatchId: string | null,
  matchId: string,
  previousPeerNames: string[],
  previousMeta: SharedRoomMatchMeta,
  incomingPeerNames: string[],
  incomingMeta: SharedRoomMatchMeta
): { peerNames: string[]; meta: SharedRoomMatchMeta } {
  const sameMatch = previousMatchId === matchId
  const basePeerNames = sameMatch ? previousPeerNames : []
  const peerNames = incomingPeerNames.length > 0 ? incomingPeerNames : basePeerNames
  /** BrSE may send title before `matchId`; do not drop `metaRef` title on a new offer open. */
  const carryMeta = sameMatch
    ? previousMeta
    : previousMeta.peerMeetingTitle?.trim()
      ? { peerMeetingTitle: previousMeta.peerMeetingTitle }
      : {}
  const meta = mergePeerMeetingMeta(carryMeta, incomingMeta)
  return { peerNames, meta }
}

function mergeJoinedRoster(
  current: JoinedOfferState,
  message: RoomSnapshotMessage
): JoinedOfferState | null {
  return mergeRoomSnapshot(current, message, {
    membersFieldPresent: message.membersFieldPresent,
  })
}

function roomIdMatchesFrame(
  frameRoomId: string | undefined,
  storedRoomId: string | null | undefined,
  state: SharedRoomOfferState
): boolean {
  if (!frameRoomId) return false
  if (storedRoomId === frameRoomId) return true
  return state.kind === 'joined' && state.roomId === frameRoomId
}

/**
 * Drives the shared-room offer the AI service makes when it hears two machines
 * on the same call (BrSE-AI plan 67), plus rejoin after Không / Leave (handoff 84).
 */
export function useSharedRoomOffer({
  sendReply,
  sendRejoin,
  rejoin,
  onJoined,
  onPersistedRoomRestore,
  currentRoomId,
  initialDismissedMatch = null,
  onDismissedMatchChange,
  onRejoinAbandoned,
}: UseSharedRoomOfferOptions) {
  const [state, setState] = useState<SharedRoomOfferState>({ kind: 'idle' })
  const [lastDismissedMatch, setLastDismissedMatch] = useState<SharedRoomDismissedMatch | null>(
    () => (currentRoomId ? null : initialDismissedMatch)
  )
  const lastDismissedMatchRef = useRef<SharedRoomDismissedMatch | null>(lastDismissedMatch)
  const peerNamesRef = useRef<string[]>([])
  const metaRef = useRef<SharedRoomMatchMeta>(EMPTY_META)
  const lastMatchIdRef = useRef<string | null>(null)
  /** Title BrSE may send before `matchId` on the opening `match_found` frame. */
  const pendingPeerMeetingTitleRef = useRef<string | null>(null)
  /**
   * Match ids that already received `match_closed` with `left`/`changed`, so a
   * later `expired` echo for the same id does not toast over a new offer.
   */
  const closedWithoutChipRef = useRef<Set<string>>(new Set())
  const stateRef = useRef<SharedRoomOfferState>({ kind: 'idle' })
  const optionsRef = useRef({
    sendReply,
    sendRejoin,
    rejoin,
    onJoined,
    onPersistedRoomRestore,
    onDismissedMatchChange,
    onRejoinAbandoned,
  })
  optionsRef.current = {
    sendReply,
    sendRejoin,
    rejoin,
    onJoined,
    onPersistedRoomRestore,
    onDismissedMatchChange,
    onRejoinAbandoned,
  }
  const currentRoomIdRef = useRef(currentRoomId)
  currentRoomIdRef.current = currentRoomId

  const publishDismissed = useCallback((snapshot: SharedRoomDismissedMatch | null) => {
    setLastDismissedMatch(snapshot)
    lastDismissedMatchRef.current = snapshot
    optionsRef.current.onDismissedMatchChange?.(snapshot)
  }, [])

  const signalRejoinAbandoned = useCallback((rejoinRoomId: string | undefined) => {
    if (rejoinRoomId) optionsRef.current.onRejoinAbandoned?.()
  }, [])

  useEffect(() => {
    if (!currentRoomId) return
    setState((current) => {
      if (current.kind !== 'idle') return current
      optionsRef.current.onPersistedRoomRestore?.()
      return { kind: 'joined', roomId: currentRoomId, peerNames: [], meta: EMPTY_META }
    })
    publishDismissed(null)
  }, [currentRoomId, publishDismissed])

  /** Waiting Yes with no auto_join within 15s → failed, keep the Join chip. */
  useEffect(() => {
    if (state.kind !== 'waiting') return undefined
    const waiting = state
    const timer = window.setTimeout(() => {
      if (stateRef.current.kind !== 'waiting') return
      publishDismissed({
        source: waiting.rejoinRoomId ? 'left' : 'declined',
        matchId: waiting.rejoinRoomId ? undefined : waiting.matchId,
        roomId: waiting.rejoinRoomId,
        peerNames: waiting.peerNames,
        meta: waiting.meta,
      })
      signalRejoinAbandoned(waiting.rejoinRoomId)
      setState({ kind: 'closed', reason: 'failed' })
    }, SHARED_ROOM_JOIN_WAIT_MS)
    return () => window.clearTimeout(timer)
  }, [state, publishDismissed, signalRejoinAbandoned])

  const applyPendingPeerTitle = (meta: SharedRoomMatchMeta): SharedRoomMatchMeta => {
    const pending = pendingPeerMeetingTitleRef.current?.trim()
    if (!pending || meta.peerMeetingTitle?.trim()) return meta
    pendingPeerMeetingTitleRef.current = null
    return { ...meta, peerMeetingTitle: pending }
  }

  const handleEvent = useCallback((frame: unknown) => {
    const message = readFrame(frame)
    if (!message) return

    const parsedTitle = message.meta.peerMeetingTitle?.trim()
    if (parsedTitle) pendingPeerMeetingTitleRef.current = parsedTitle

    if (message.event === 'sharedroom.match_found' && !message.matchId) {
      if (parsedTitle) {
        metaRef.current = mergePeerMeetingMeta(metaRef.current, message.meta)
      }
      const patched = applyPeerTitlePatch(stateRef.current, message)
      if (patched) {
        peerNamesRef.current = patched.peerNames
        metaRef.current = patched.meta
        setState(patched)
      }
      return
    }

    if (message.event === 'sharedroom.match_found' && message.matchId) {
      const current = stateRef.current
      const dismissed = lastDismissedMatchRef.current
      if (
        dismissed?.matchId === message.matchId &&
        (current.kind === 'idle' || current.kind === 'closed')
      ) {
        const merged = mergeMatchFoundPayload(
          dismissed.matchId,
          message.matchId,
          dismissed.peerNames,
          dismissed.meta,
          message.peerNames,
          message.meta
        )
        const peerNames = merged.peerNames
        const meta = applyPendingPeerTitle(merged.meta)
        lastMatchIdRef.current = message.matchId
        peerNamesRef.current = peerNames
        metaRef.current = meta
        publishDismissed({
          source: dismissed.source,
          matchId: dismissed.matchId,
          roomId: dismissed.roomId,
          peerNames,
          meta,
        })
        return
      }
      const openSameMatch =
        isOpen(current) && current.matchId === message.matchId
      const merged = mergeMatchFoundPayload(
        openSameMatch ? message.matchId : lastMatchIdRef.current,
        message.matchId,
        openSameMatch ? current.peerNames : peerNamesRef.current,
        openSameMatch ? current.meta : metaRef.current,
        message.peerNames,
        message.meta
      )
      const peerNames = merged.peerNames
      const meta = applyPendingPeerTitle(merged.meta)
      lastMatchIdRef.current = message.matchId
      peerNamesRef.current = peerNames
      metaRef.current = meta
      publishDismissed(null)
      const kind = message.role === 'joiner' ? 'requested' : 'offered'
      setState({
        kind,
        matchId: message.matchId,
        peerNames,
        meta,
      })
      return
    }

    if (
      message.event === 'sharedroom.rejoin_closed' ||
      (message.event === 'sharedroom.match_closed' && message.reason === 'expired')
    ) {
      const waitingRejoin =
        stateRef.current.kind === 'waiting' ? stateRef.current.rejoinRoomId : undefined
      if (
        message.event === 'sharedroom.rejoin_closed' &&
        waitingRejoin &&
        message.roomId &&
        message.roomId !== waitingRejoin
      ) {
        return
      }
      const reason =
        message.reason === 'failed'
          ? 'failed'
          : message.reason === 'expired'
            ? 'expired'
            : message.event === 'sharedroom.rejoin_closed'
              ? 'failed'
              : 'expired'
      if (
        reason === 'expired' &&
        message.matchId &&
        closedWithoutChipRef.current.has(message.matchId)
      ) {
        return
      }
      if (reason === 'failed') {
        const dismissed = lastDismissedMatchRef.current
        const current = stateRef.current
        if (isOpen(current)) {
          const rejoinRoomId =
            current.kind === 'waiting' ? current.rejoinRoomId : undefined
          publishDismissed({
            source: rejoinRoomId ? 'left' : 'declined',
            matchId: rejoinRoomId ? undefined : current.matchId,
            roomId: rejoinRoomId,
            peerNames: current.peerNames,
            meta: metaFromState(current),
          })
          signalRejoinAbandoned(rejoinRoomId)
        } else if (dismissed) {
          publishDismissed(dismissed)
        }
        setState({ kind: 'closed', reason: 'failed' })
        return
      }
      publishDismissed(null)
      setState({ kind: 'closed', reason: 'expired' })
      return
    }

    if (message.event === 'sharedroom.match_closed') {
      const current = stateRef.current
      if (current.kind === 'joined') {
        const merged = mergeJoinedRoster(current, message)
        if (merged) {
          peerNamesRef.current = merged.peerNames
          metaRef.current = merged.meta
          setState(merged)
          return
        }
        if (message.reason === 'left') return
      }
      const open = isOpen(current)
      const pendingClose =
        !open &&
        (message.reason === 'declined' || message.reason === 'failed') &&
        message.matchId &&
        message.matchId === lastMatchIdRef.current

      if (message.reason === 'declined' || message.reason === 'failed') {
        const showing = open ? current.matchId : null
        const ignored =
          showing === null && !pendingClose
            ? 'ignored, no offer is open here'
            : message.matchId && showing && message.matchId !== showing
              ? `ignored, this machine is showing ${showing}`
              : 'shown to the reader'
        console.warn(
          `Shared-room match ${message.matchId ?? '(unnamed)'} closed: ${message.reason} — ${ignored}`
        )
      }

      const declinedEchoAfterLocalNo =
        current.kind === 'idle' &&
        message.reason === 'declined' &&
        message.matchId &&
        message.matchId === lastMatchIdRef.current &&
        lastDismissedMatchRef.current?.matchId === message.matchId
      if (declinedEchoAfterLocalNo) return

      if (!open && !pendingClose) {
        if (
          (message.reason === 'left' || message.reason === 'changed') &&
          message.matchId
        ) {
          closedWithoutChipRef.current.add(message.matchId)
        }
        return
      }
      if (open && message.matchId && message.matchId !== current.matchId) return

      if (message.reason === 'declined' || message.reason === 'failed') {
        const matchId = open ? current.matchId : (lastMatchIdRef.current ?? message.matchId!)
        const peerNames = open ? current.peerNames : peerNamesRef.current
        const meta = open ? metaFromState(current) : metaRef.current
        // Set snapshot before `closed` so the UI effect sees both in one commit.
        publishDismissed({
          source: 'declined',
          matchId,
          peerNames,
          meta,
        })
        setState({ kind: 'closed', reason: message.reason })
        return
      }
      if (
        (message.reason === 'left' || message.reason === 'changed') &&
        message.matchId
      ) {
        closedWithoutChipRef.current.add(message.matchId)
      }
      peerNamesRef.current = []
      metaRef.current = EMPTY_META
      pendingPeerMeetingTitleRef.current = null
      setState({ kind: 'idle' })
      return
    }

    if (message.event === 'sharedroom.auto_join' && message.roomId) {
      if (message.matchId) lastMatchIdRef.current = message.matchId
      // A move, not a rename: read where this machine was before the state
      // below replaces it, so the name of the room it is leaving is not carried
      // into the room it is entering.
      const previousRoomId =
        stateRef.current.kind === 'joined' ? stateRef.current.roomId : currentRoomIdRef.current
      const movedRoom = Boolean(previousRoomId) && previousRoomId !== message.roomId
      if (movedRoom && !parsedTitle) pendingPeerMeetingTitleRef.current = null
      optionsRef.current.onJoined(message.roomId, { onlyWhenUnset: false })
      optionsRef.current.rejoin(message.roomId)
      publishDismissed(null)
      const peerNames =
        message.peerNames.length > 0 ? message.peerNames : peerNamesRef.current
      const meta = {
        ...carryMetaIntoRoom(metaRef.current, previousRoomId, message.roomId),
        ...message.meta,
      }
      peerNamesRef.current = peerNames
      metaRef.current = meta
      setState({
        kind: 'joined',
        roomId: message.roomId,
        peerNames,
        meta,
      })
      return
    }

    if (message.event === 'sharedroom.members' && message.roomId) {
      const current = stateRef.current
      const roomMatches = roomIdMatchesFrame(
        message.roomId,
        currentRoomIdRef.current,
        current
      )
      const openOfferNeedsTitle =
        isOpen(current) &&
        !roomMatches &&
        Boolean(message.meta.peerMeetingTitle?.trim())
      if (!roomMatches && !openOfferNeedsTitle) {
        return
      }
      if (
        current.kind === 'joined' ||
        current.kind === 'offered' ||
        current.kind === 'requested' ||
        current.kind === 'waiting'
      ) {
        const merged = mergeRoomSnapshot(current, message, {
          membersFieldPresent: message.membersFieldPresent,
        })
        if (merged) {
          peerNamesRef.current = merged.peerNames
          metaRef.current = merged.meta
          setState(merged)
        }
      }
      return
    }

    if (message.event === 'sharedroom.auto_joined' && message.roomId) {
      if (message.matchId) lastMatchIdRef.current = message.matchId
      // The server names everybody else in the room. This machine was never
      // asked, so it has no offer of its own to take names from; an older
      // server sends none, and the names of an earlier offer stand in.
      const peerNames =
        message.peerNames.length > 0 ? message.peerNames : peerNamesRef.current
      const previousRoomId =
        stateRef.current.kind === 'joined' ? stateRef.current.roomId : currentRoomIdRef.current
      const meta = {
        ...carryMetaIntoRoom(metaRef.current, previousRoomId, message.roomId),
        ...message.meta,
      }
      peerNamesRef.current = peerNames
      metaRef.current = meta
      optionsRef.current.onJoined(message.roomId, { onlyWhenUnset: true })
      publishDismissed(null)
      setState({
        kind: 'joined',
        roomId: message.roomId,
        peerNames,
        meta,
      })
      return
    }

    const titlePatch = applyPeerTitlePatch(stateRef.current, message)
    if (titlePatch) {
      peerNamesRef.current = titlePatch.peerNames
      metaRef.current = titlePatch.meta
      setState(titlePatch)
    }
  }, [publishDismissed, signalRejoinAbandoned])

  const answer = useCallback(
    (accept: boolean): boolean => {
      const current = stateRef.current
      if (current.kind === 'requested') {
        if (accept) return false
        publishDismissed({
          source: 'declined',
          matchId: current.matchId,
          peerNames: current.peerNames,
          meta: metaFromState(current),
        })
        optionsRef.current.sendReply(current.matchId, false)
        setState({ kind: 'idle' })
        return true
      }
      if (current.kind !== 'offered') return false
      if (!optionsRef.current.sendReply(current.matchId, accept)) {
        setState({ kind: 'idle' })
        return false
      }
      if (!accept) {
        publishDismissed({
          source: 'declined',
          matchId: current.matchId,
          peerNames: current.peerNames,
          meta: metaFromState(current),
        })
        setState({ kind: 'idle' })
        return true
      }
      setState({
        kind: 'waiting',
        matchId: current.matchId,
        peerNames: current.peerNames,
        meta: current.meta,
      })
      return true
    },
    [publishDismissed]
  )

  const accept = useCallback((): boolean => answer(true), [answer])
  const decline = useCallback(() => answer(false), [answer])

  /** Cancels a merge/rejoin the reader already accepted but that has not finished yet. */
  const withdrawFromMerge = useCallback((): boolean => {
    const current = stateRef.current
    if (current.kind !== 'waiting') return false
    if (current.rejoinRoomId) {
      // No cancel-rejoin on the wire — only stop waiting locally and keep the chip.
      publishDismissed({
        source: 'left',
        roomId: current.rejoinRoomId,
        peerNames: current.peerNames,
        meta: metaFromState(current),
      })
      signalRejoinAbandoned(current.rejoinRoomId)
      setState({ kind: 'idle' })
      return true
    }
    if (!optionsRef.current.sendReply(current.matchId, false)) {
      setState({ kind: 'idle' })
      return false
    }
    publishDismissed({
      source: 'declined',
      matchId: current.matchId,
      peerNames: current.peerNames,
      meta: metaFromState(current),
    })
    setState({ kind: 'idle' })
    return true
  }, [publishDismissed, signalRejoinAbandoned])

  /**
   * Re-accept after the reader dismissed the modal (Không) or left the room.
   * Declined → `match_reply`; left → `sharedroom.rejoin`.
   */
  const acceptDismissedMatch = useCallback(() => {
    const dismissed = lastDismissedMatchRef.current
    if (!dismissed) return false
    if (dismissed.source === 'left') {
      const roomId = dismissed.roomId
      if (!roomId || !optionsRef.current.sendRejoin(roomId)) return false
      setState({
        kind: 'waiting',
        matchId: dismissed.matchId ?? roomId,
        peerNames: dismissed.peerNames,
        meta: dismissed.meta,
        rejoinRoomId: roomId,
      })
      // Keep dismissed snapshot (and sessionStorage) until join completes or times out.
      return true
    }
    if (!dismissed.matchId) return false
    if (!optionsRef.current.sendReply(dismissed.matchId, true)) return false
    setState({
      kind: 'waiting',
      matchId: dismissed.matchId,
      peerNames: dismissed.peerNames,
      meta: dismissed.meta,
    })
    return true
  }, [])

  const dismiss = useCallback(() => {
    peerNamesRef.current = []
    metaRef.current = EMPTY_META
    pendingPeerMeetingTitleRef.current = null
    setState({ kind: 'idle' })
  }, [])

  const clearDismissedMatch = useCallback(() => publishDismissed(null), [publishDismissed])

  /**
   * After leaving a room, keep enough context to reopen the detection modal.
   * Hosts (no prior matchId) still get a chip via `roomId`.
   */
  const rememberForRejoin = useCallback(
    (roomId: string | null | undefined) => {
      const current = stateRef.current
      const leftRoomId =
        roomId?.trim() || (current.kind === 'joined' ? current.roomId.trim() : '')
      if (!leftRoomId) return
      const peerNames = current.kind === 'joined' ? current.peerNames : peerNamesRef.current
      const meta = current.kind === 'joined' ? current.meta : metaRef.current
      publishDismissed({
        source: 'left',
        matchId: lastMatchIdRef.current ?? undefined,
        roomId: leftRoomId,
        peerNames,
        meta,
      })
    },
    [publishDismissed]
  )

  /** Clears live offer state when the reader opens a different meeting. */
  const resetForLiveMeeting = useCallback(
    (snapshot: SharedRoomDismissedMatch | null) => {
      if (currentRoomIdRef.current) {
        publishDismissed(null)
        return
      }
      setState({ kind: 'idle' })
      peerNamesRef.current = []
      metaRef.current = EMPTY_META
      lastMatchIdRef.current = null
      pendingPeerMeetingTitleRef.current = null
      closedWithoutChipRef.current.clear()
      publishDismissed(snapshot)
    },
    [publishDismissed]
  )

  stateRef.current = state
  lastDismissedMatchRef.current = lastDismissedMatch

  return {
    state,
    lastDismissedMatch,
    handleEvent,
    accept,
    decline,
    withdrawFromMerge,
    acceptDismissedMatch,
    dismiss,
    clearDismissedMatch,
    rememberForRejoin,
    resetForLiveMeeting,
  }
}
